<template>
  <aside class="soldera-proof h-100">
    <div>
      <p class="proof-label">
        {{ $t("billing.proof.account_executive_label") }}
      </p>
      <div class="d-flex align-items-center gap-3">
        <div class="host-avatars" aria-hidden="true">
          <img
            v-for="host in HOSTS"
            :key="host.name"
            :src="host.photo"
            alt=""
            class="host-avatar"
            loading="lazy"
          />
        </div>
        <div>
          <p class="host-names mb-0">{{ hostNames }}</p>
          <p class="proof-muted small mb-0">
            {{ $t("billing.proof.account_executive_role") }}
          </p>
        </div>
      </div>
    </div>

    <div>
      <h3 class="proof-title font-serif mb-2">
        {{ $t("billing.proof.title") }}
      </h3>
      <p class="proof-muted mb-0">{{ $t("billing.proof.body") }}</p>
    </div>

    <div class="d-flex flex-wrap gap-4">
      <div v-for="stat in STATS" :key="stat.labelKey">
        <div class="proof-stat font-serif">{{ stat.value }}</div>
        <div class="proof-muted small">{{ $t(stat.labelKey) }}</div>
      </div>
    </div>

    <div v-for="row in logoRows" :key="row.labelKey">
      <p class="proof-label">{{ $t(row.labelKey) }}</p>
      <LogoMarqueeComponent :logos="row.logos" :smallPrint="row.smallPrint" />
      <p v-if="row.note" class="small mt-2 mb-0">
        <a
          :href="row.note.url"
          target="_blank"
          rel="noopener noreferrer"
          class="proof-link"
        >
          {{ $t(row.note.key) }}
          <FontAwesomeIcon icon="arrow-up-right-from-square" class="ms-1" />
        </a>
      </p>
    </div>

    <div v-if="teamAsNames" data-testid="team-names">
      <p class="proof-label">{{ $t(TEAM_ROW.labelKey) }}</p>
      <ul class="team-names list-unstyled small mb-0">
        <li v-for="logo in TEAM_ROW.logos" :key="logo.name">
          <a
            :href="logo.url"
            target="_blank"
            rel="noopener noreferrer"
            class="team-name"
          >
            {{ logo.name }}
          </a>
        </li>
      </ul>
      <p class="small mt-2 mb-0">
        <a
          :href="TEAM_ROW.note!.url"
          target="_blank"
          rel="noopener noreferrer"
          class="proof-link"
        >
          {{ $t(TEAM_ROW.note!.key) }}
          <FontAwesomeIcon icon="arrow-up-right-from-square" class="ms-1" />
        </a>
      </p>
    </div>
  </aside>
</template>

<script setup lang="ts">
import LogoMarqueeComponent from "@/components/proof/LogoMarqueeComponent.vue";
import {
  CUSTOMER_LOGOS,
  RECOGNISED_LOGOS,
  TEAM_LOGOS,
  type MarqueeLogo,
} from "@/components/proof/solderaLogos";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Why buy from Soldera, as the dark panel of https://www.soldera.org/demo shows it: the people who will
 * guide the customer, how the service works, platform numbers, customers and the team. Shared by the plan
 * and demo steps of the onboarding modal; keep its claims to the approved ones on soldera.org.
 */
const HOSTS = [
  {
    name: "Roger",
    photo: "https://www.soldera.org/images/demo/host-roger.jpg",
  },
  {
    name: "Ronny",
    photo: "https://www.soldera.org/images/demo/host-ronny.jpg",
  },
  {
    name: "Arturas",
    photo: "https://www.soldera.org/images/demo/host-arturas.jpg",
  },
  {
    name: "Stenver",
    photo: "https://www.soldera.org/images/demo/host-stenver.jpg",
  },
];

interface LogoRow {
  labelKey: string;
  /** A line under the logos, linking to where the claim can be checked. */
  note?: { key: string; url: string };
  smallPrint?: boolean;
  logos: MarqueeLogo[];
}

const TEAM_ROW: LogoRow = {
  labelKey: "billing.proof.team_label",
  note: { key: "billing.proof.team", url: "https://www.aib-net.org" },
  logos: TEAM_LOGOS,
};

const LOGO_ROWS: LogoRow[] = [
  {
    labelKey: "billing.proof.used_by",
    logos: CUSTOMER_LOGOS,
  },
  {
    labelKey: "billing.proof.recognised_backed_by",
    smallPrint: true,
    logos: RECOGNISED_LOGOS,
  },
  TEAM_ROW,
];

// Approved floors, written with space-separated thousands as the design system asks.
const STATS = [
  { value: "1 000+", labelKey: "billing.proof.stats.clients" },
  { value: "6 000+", labelKey: "billing.proof.stats.plants" },
  { value: "30+", labelKey: "billing.proof.stats.registries" },
];

const props = withDefaults(
  defineProps<{
    /** The team as a short list of names at the bottom instead of a row of logos, for a quieter panel. */
    teamAsNames?: boolean;
  }>(),
  { teamAsNames: false }
);

const { locale } = useI18n();

const logoRows = computed(() =>
  props.teamAsNames ? LOGO_ROWS.filter((row) => row !== TEAM_ROW) : LOGO_ROWS
);

const hostNames = computed(() =>
  new Intl.ListFormat(locale.value, { type: "disjunction" }).format(
    HOSTS.map((host) => host.name)
  )
);
</script>

<style scoped lang="scss">
// The dark panel of soldera.org/demo, in the app design system's tokens.
.soldera-proof {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2.5rem 2rem;
  border-radius: 12px;
  background: #2d3e37;
  color: #ffffff;
}

.proof-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #b8d0bf;
  margin-bottom: 0.75rem;
}

.proof-title {
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1.2;
  color: #ffffff;
}

.proof-muted {
  color: #b8d0bf;
}

.proof-stat {
  font-size: 1.95rem;
  font-weight: 600;
  line-height: 1.1;
}

.proof-link {
  color: #b8d0bf;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover,
  &:focus-visible {
    color: #ffffff;
  }
}

.team-names {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0;

  li:not(:last-child)::after {
    content: "·";
    margin: 0 0.5rem;
    color: #b8d0bf;
  }
}

.team-name {
  color: #ffffff;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
}

.host-names {
  font-weight: 600;
}

.host-avatars {
  display: flex;
  flex: 0 0 auto;
}

.host-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #2d3e37;
  background: #b8d0bf;

  & + & {
    margin-left: -16px;
  }
}
</style>
